<template>
  <section>
    <template v-if="paymentData && paymentData.clientSecret">
      <StripeElementPayment
        ref="paymentRef"
        :pk="pk"
        :elements-options="elementsOptions"
        :confirm-params="confirmParams"
        mode="payment"
        :key="key"
      ></StripeElementPayment>
    </template>
    <div>
      <label for="promo-code">
        {{ $t("reservation.promocode") }}
      </label>
      <div class="input-group m-0 gap-2">
        <input
          id="promo-code"
          type="text"
          class="form-control"
          v-model="promoCode"
          :placeholder="$t('reservation.enterCode')"
        />
        <button
          id="apply-btn"
          class="apply-btn"
          @click.prevent="getPaymentIntent"
          :disabled="!promoCode"
        >
          {{ $t("reservation.apply") }}
        </button>
      </div>
    </div>
    <button id="confirm-payment" @click.prevent="checkReservation">
      {{ $t("reservation.confirmPayment") }}
    </button>
  </section>
</template>

<script>
import GlobalMixin from "../../GlobalMixin";
import { StripeElementPayment } from "@vue-stripe/vue-stripe";

export default {
  name: "Stripe",
  mixins: [GlobalMixin],
  components: {
    StripeElementPayment,
  },
  data() {
    return {
      paymentData: {},
      promoCode: null,
      key: 0,
      pk: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
      elementsOptions: {
        locale: localStorage.lang,
        appearance: {},
      },
      confirmParams: {
        return_url: "http://localhost:8080/BookedClasses", // success url
      },
      // Default payment method
      paymentMethod: "card",
    };
  },
  mounted() {
    // Call getPaymentIntent function
    this.getPaymentIntent();

    // Get selected payment method after one second to load the element first
    setTimeout(() => {
      const { element } = this.$refs.paymentRef;
      element.addEventListener("change", (event) => {
        this.paymentMethod = event.value.type;
      });
    }, 2000);
  },
  methods: {
    /**
     * Get all information required for payment process with stripe
     * Generate Client Secret
     * @returns Promise<void>
     */
    async getPaymentIntent() {
      this.$store.dispatch("savePaymentInfo", {
        ...this.$store.getters.GET_PAYMENT_INFO,
        isLoading: true,
      });
      const body = {
        first_course: this.$store.getters.GET_SELECTED_COURSES[0],
        second_course: this.$store.getters.GET_SELECTED_COURSES[1],
        program: this.$store.getters.GET_SELECTED_PROGRAM?.id,
        tutor: this.$route.params.id,
        student_available_time_slots: this.$store.getters.GET_SELECTED_SLOTS,
        promo_code: this.promoCode || undefined,
      };
      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_APIEndPoint}payments/create-intent/`,
          body,
          this.headers
        );

        this.paymentData = await response.data.data;
        this.$store.dispatch("savePaymentInfo", {
          payment: await response.data.data,
          isLoading: false,
        });
        this.elementsOptions.clientSecret = await response.data.data
          .clientSecret;
      } catch (e) {
        this.$store.dispatch("savePaymentInfo", {
          ...this.$store.getters.GET_PAYMENT_INFO,
          isLoading: false,
        });
        this.formatErrors(e);
      }
    },
    /**
     * Reserve a session based on client secret
     * @returns void
     */
    reserveSessions() {
      const body = {
        program: this.$store.getters.GET_SELECTED_PROGRAM?.id,
        first_course: this.$store.getters.GET_SELECTED_COURSES[0],
        second_course: this.$store.getters.GET_SELECTED_COURSES[1],
        student_available_time_slots: this.$store.getters.GET_SELECTED_SLOTS,
        tutor: +this.$route.params.id,
        payment_intent: this.elementsOptions.clientSecret,
        promo_code: this.promoCode || undefined,
        student: this.$store.getters.StateUser.user.id,
        institute: +this.$store.getters.StateUser.user.username.at(-1),
      };
      this.axios
        .post(
          `${process.env.VUE_APP_APIEndPoint}reserve/course/`,
          body,
          this.headers
        )
        .then((res) => {
          if (res.data.success) {
            this.promoCode = "";
            Swal.fire({
              icon: "success",
              title: this.$t("messages.success"),
              text: this.$t("messages.reservation"),
              confirmButtonText: this.$t("messages.confirmation"),
            }).then(() => {
              this.$router.push({ name: "BookedClasses" });
            });
          }
        })
        .catch((err) => {
          this.formatErrors(err);
        });
    },

    /**
     * Check if user has promocode or not and invoke a function for it
     * @returns void
     */
    async checkReservation() {
      if (!this.paymentData.amount_data.net_amount) {
        this.reserveSessions();
      } else await this.confirmPayment();
    },

    /**
     * Confirm payment and if it's confirmed reserve a session
     * @returns void
     */
    async confirmPayment() {
      try {
        const { error } = await this.payWithSelectedPaymentMethod();

        if (error) return this.showAlert(error.message, "error");
        return this.reserveSessions();
      } catch (e) {
        this.formatErrors(e);
      }
    },

    /**
     * Pay with selected method
     * @returns Promise<void>
     */
    async payWithSelectedPaymentMethod() {
      const { stripe, element } = this.$refs.paymentRef;

      switch (this.paymentMethod) {
        case "card":
          return await stripe.confirmCardPayment(
            this.elementsOptions.clientSecret,
            {
              payment_method: { element },
            }
          );
        case "cashapp":
          return await stripe.confirmCashappPayment(
            this.elementsOptions.clientSecret,
            {
              payment_method: { element },
              return_url: "http://localhost:8080/BookedClasses", // success url
            }
          );
        case "us_bank_account":
          return await stripe.confirmUsBankAccountPayment(
            this.elementsOptions.clientSecret,
            {
              payment_method: { element },
            }
          );
        default:
          throw new Error("Error in selecting Payment Method");
      }
    },
  },
  watch: {
    /**
     * Change locale of stripe element if locale is changed in the app
     * @param newVal string
     */
    "$i18n.locale"(newVal) {
      this.elementsOptions.locale = newVal;
      this.headers.headers["accept-language"] = localStorage.lang;
      this.getPaymentIntent();
      // Re-render stripe component
      ++this.key;
    },
  },
};
</script>

<style lang="scss" scoped>
div {
  font-family: $font-family;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 27px;
  color: #5b5b5b;
  margin: 36px 0;
  label {
    margin-bottom: 10px;
  }
  input {
    font-weight: 400;
    display: block;
    background: #ffffff;
    border: 1px solid #d9d9d9;
    border-radius: 10px;
    padding: 10px 20px;
    width: 100%;
  }
  &.input-group {
    max-width: 440px;
  }
}
button {
  max-width: 440px;
  width: 100%;
  padding: 15px;
  color: $white;
  background: $green;
  border-radius: 8px;
  &:hover {
    background: $hover-green;
  }

  &.apply-btn {
    all: unset;
    background: #b4bbc5;
    border-radius: 8px !important;
    padding: 17px 24px;
    cursor: pointer;
    color: $white;
    &:hover {
      background: #949aa1;
    }
    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}
</style>
